import React, { MouseEvent, useCallback, useMemo } from "react";

import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import isToday from "date-fns/isToday";
import isTomorrow from "date-fns/isTomorrow";
import { defineMessages, useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";

import ActivityAssignee from "@app/component/activity/ActivityAssignee";
import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import styles from "./ActivityItem.module.scss";

const messages = defineMessages({
  allDay: {
    id: "dashboard.cards.upcomingActivities.list.allDay",
    defaultMessage: "All Day",
    description: "All Day",
  },
  inDays: {
    id: "dashboard.cards.upcomingActivities.list.inDays",
    defaultMessage: "In {count} {count, plural, one {day} other {days}}",
    description: "In how many days text",
  },
  today: {
    id: "dashboard.cards.upcomingActivities.list.today",
    defaultMessage: "Today",
    description: "Today",
  },
  tomorrow: {
    id: "dashboard.cards.upcomingActivities.list.tomorrow",
    defaultMessage: "Tomorrow",
    description: "Tomorrow",
  },
});

interface Props {
  activity: Activity;
  onClick: (id: Activity["id"], e: MouseEvent) => void;
}

const ActivityItem: React.FC<Props> = ({ activity, onClick }) => {
  const intl = useIntl();

  const title = useMemo(
    () => activity.account?.name ?? activity.contact?.name ?? activity.deal?.name ?? "",
    [activity.account, activity.contact, activity.deal]
  );

  const timeFormat = SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm";
  const startDateFormatted = activity.allDay
    ? intl.formatMessage(messages.allDay)
    : activity.startAt
    ? formatDate(parseApiDate(activity.startAt), timeFormat)
    : "";

  const activityInfo = useMemo(
    () =>
      [
        activity.crmActivityType.name,
        startDateFormatted,
        ...(activity.startAt ? [formatDate(parseApiDate(activity.startAt), "PPP")] : []),
        ...(title.length ? [activity.name] : []),
      ].join(" • "),
    [
      activity.crmActivityType.name,
      activity.name,
      activity.startAt,
      startDateFormatted,
      title.length,
    ]
  );

  const dateText = useMemo(() => {
    if (activity.startAt) {
      if (isToday(parseApiDate(activity.startAt))) {
        return intl.formatMessage(messages.today);
      } else if (isTomorrow(parseApiDate(activity.startAt))) {
        return intl.formatMessage(messages.tomorrow);
      } else {
        return intl.formatMessage(messages.inDays, {
          count: differenceInCalendarDays(parseApiDate(activity.startAt), new Date()),
        });
      }
    }
    return "";
  }, [activity.startAt, intl]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick(activity.id, e);
    },
    [activity.id, onClick]
  );

  return (
    <div className={styles.container} onClick={handleClick}>
      <ActivityTypeIcon activityType={activity.crmActivityType} className={styles.typeIcon} />
      <div className={styles.data}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>{title.length ? title : activity.name}</span>
        </div>
      </div>
      <div className={styles.date}>{dateText}</div>
      <div className={styles.detailsContainer}>
        <ActivityAssignee activity={activity} className={styles.assignee} />
        <span className={styles.details}>{activityInfo}</span>
      </div>
    </div>
  );
};

export default ActivityItem;
